import { beaaQuote } from '@/mocks/beaa';
import {
  beaaAssistantFallback,
  beaaAssistantGreeting,
  beaaAssistantKnowledge,
} from '@/mocks/beaaAssistant';
import { beaaChatQuickActions, beaaChatSuggestions } from '@/mocks/beaaChat';
import {
  beaaAdminAcessoGreeting,
  beaaAdminAcessoSuggestions,
  beaaAdminFallback,
  beaaAdminGreeting,
  beaaAdminKnowledge,
  beaaAdminSuggestions,
} from '@/mocks/beaaAssistantAdmin';

export interface BeaaAssistantAction {
  label: string;
  icon: string;
  to?: string;
  href?: string;
}

export interface BeaaAssistantEntry {
  id: string;
  keywords: string[];
  answer: string;
  actions: BeaaAssistantAction[];
}

export interface BeaaSuggestion {
  id: string;
  icon: string;
  label: string;
}

export interface BeaaQuickAction {
  label: string;
  description: string;
  icon: string;
  to: string;
}

/** Módulo em que o operador está no Console — mostrado como aviso no painel. */
export interface BeaaModuloContexto {
  label: string;
  icon: string;
  to?: string;
}

/**
 * Perfil de conteúdo da Beaa. Cada contexto (portal do cidadão ou Console
 * Administrativo) carrega o seu próprio perfil, com respostas, sugestões e
 * atalhos diferentes — o painel e o mascote são os mesmos componentes.
 */
export interface BeaaAssistantProfile {
  greeting: string;
  suggestions: BeaaSuggestion[];
  knowledge: BeaaAssistantEntry[];
  fallback: BeaaAssistantEntry;
  headerSubtitle: string;
  ariaLabel: string;
  menuTitle: string;
  menuSubtitle: string;
  quote?: string;
  quickActions: BeaaQuickAction[];
  whatsapp?: { href: string; label: string };
  menuChatTitle: string;
  menuChatSubtitle: string;
  /** Módulo atual (ex.: Animais) — quando presente, o painel mostra um aviso. */
  contextoModulo?: BeaaModuloContexto;
}

const portalKnowledge = beaaAssistantKnowledge as unknown as BeaaAssistantEntry[];
const portalFallback = beaaAssistantFallback as unknown as BeaaAssistantEntry;
const consoleKnowledge = beaaAdminKnowledge as unknown as BeaaAssistantEntry[];
const consoleFallback = beaaAdminFallback as unknown as BeaaAssistantEntry;

/** Perfil do cidadão — usado em todo o portal público e na Minha Área. */
export const beaaPortalProfile: BeaaAssistantProfile = {
  greeting: beaaAssistantGreeting,
  suggestions: beaaChatSuggestions as unknown as BeaaSuggestion[],
  knowledge: portalKnowledge,
  fallback: portalFallback,
  headerSubtitle: 'Assistente do bem-estar animal',
  ariaLabel: 'Conversa com a Beaa',
  menuTitle: 'Oi, eu sou a Beaa',
  menuSubtitle: 'Posso te ajudar por aqui',
  quote: beaaQuote,
  quickActions: beaaChatQuickActions as unknown as BeaaQuickAction[],
  whatsapp: {
    href: 'https://wa.me/556230302811?text=Olá!%20Falei%20com%20a%20Beaa%20no%20portal%20e%20gostaria%20de%20ajuda.',
    label: 'Falar no WhatsApp',
  },
  menuChatTitle: 'Conversa comigo',
  menuChatSubtitle: 'Tire dúvidas com a Beaa',
};

/** Perfil interno — usado apenas no Console Administrativo (ambiente técnico). */
export const beaaConsoleProfile: BeaaAssistantProfile = {
  greeting: beaaAdminGreeting,
  suggestions: beaaAdminSuggestions as unknown as BeaaSuggestion[],
  knowledge: consoleKnowledge,
  fallback: consoleFallback,
  headerSubtitle: 'Assistente do Console Administrativo',
  ariaLabel: 'Conversa com a Beaa — Console Administrativo',
  menuTitle: 'Olá, eu sou a Beaa',
  menuSubtitle: 'Posso te orientar por aqui',
  quote: 'Ambiente técnico e auditado — segurança em primeiro lugar.',
  quickActions: [],
  menuChatTitle: 'Ajuda com o Console',
  menuChatSubtitle: 'Acesso, segundo fator e módulos',
};

export const beaaConsoleAcessoProfile: BeaaAssistantProfile = {
  greeting: beaaAdminAcessoGreeting,
  suggestions: beaaAdminAcessoSuggestions as unknown as BeaaSuggestion[],
  knowledge: consoleKnowledge,
  fallback: consoleFallback,
  headerSubtitle: 'Assistente do Console Administrativo',
  ariaLabel: 'Conversa com a Beaa — ativação de acesso ao Console',
  menuTitle: 'Olá, eu sou a Beaa',
  menuSubtitle: 'Vamos ativar o seu acesso',
  quote: 'A sua senha é pessoal e intransferível — a segurança começa aqui.',
  quickActions: [],
  menuChatTitle: 'Ajuda com o seu acesso',
  menuChatSubtitle: 'Senha, política de uso e segundo fator',
};

/**
 * Perfil geral do Console — usado dentro das páginas do Console (navegação + manuais).
 * Além dos conhecimentos fixos, soma-se a ele o conteúdo dos manuais lido do banco.
 */
export const beaaConsoleGeralProfile: BeaaAssistantProfile = {
  greeting:
    'Olá! Eu sou a Beaa, assistente do Console. Posso te levar direto ao ponto: explicar cada tela e os manuais passo a passo, ajudar no acesso (senha, primeiro acesso e segundo fator) e responder dúvidas de navegação. É só escrever ou tocar numa pergunta frequente.',
  suggestions: [
    { id: 'adm-ger-cadastrar-pf', icon: 'ri-user-add-line', label: 'Como cadastro uma pessoa física?' },
    { id: 'adm-ger-inativar', icon: 'ri-toggle-line', label: 'Como inativar um cadastro?' },
    { id: 'adm-ger-exportar', icon: 'ri-download-2-line', label: 'Como exportar a lista de pessoas?' },
    { id: 'adm-ger-credenciado', icon: 'ri-shield-check-line', label: 'O que é estar credenciado?' },
    { id: 'adm-ger-modulos', icon: 'ri-apps-2-line', label: 'Quais módulos tem no Console?' },
    { id: 'adm-ger-senha', icon: 'ri-lock-password-line', label: 'Esqueci minha senha, e agora?' },
  ],
  knowledge: consoleKnowledge,
  fallback: consoleFallback,
  headerSubtitle: 'Assistente do Console Administrativo',
  ariaLabel: 'Conversa com a Beaa — Console Administrativo',
  menuTitle: 'Olá, eu sou a Beaa',
  menuSubtitle: 'Posso te orientar por aqui',
  quote: 'Precisando, eu te levo direto à tela ou ao trecho do manual.',
  quickActions: [],
  menuChatTitle: 'Ajuda com o Console',
  menuChatSubtitle: 'Telas, manuais e acesso',
};

/**
 * Atalhos contextuais do Console, por módulo.
 *
 * O menu da Beaa mostra o manual e a tela do módulo em que o operador está no
 * momento (ex.: dentro de Animais mostra "Manual/Tela de Animais"), e as
 * perguntas frequentes do painel também trocam conforme o módulo. Basta a
 * rota começar com o prefixo — então também funciona nas telas internas e no
 * próprio manual do módulo.
 */
const MODULOS_CONSOLE: {
  prefixo: string;
  contexto: BeaaModuloContexto;
  saudacao: string;
  /** Atalho para a tela principal do módulo. */
  tela: BeaaQuickAction;
  /** Atalho para o manual do módulo — só existe quando o manual já foi criado. */
  manual?: BeaaQuickAction;
  sugestoes: BeaaSuggestion[];
}[] = [
  {
    prefixo: '/admin/operacional/cadastros/animais',
    contexto: { label: 'Animais', icon: 'ri-bear-smile-line', to: '/admin/operacional/cadastros/animais' },
    saudacao:
      'Olá! Eu sou a Beaa. Estou com você no módulo de Animais. Posso explicar o cadastro, o manual passo a passo, os vínculos com tutores e cuidadores, o selo Saudável e a ficha do animal. É só escrever ou tocar numa pergunta frequente.',
    tela: {
      label: 'Tela de Animais',
      description: 'Abrir o Cadastro de Animais',
      icon: 'ri-bear-smile-line',
      to: '/admin/operacional/cadastros/animais',
    },
    manual: {
      label: 'Manual de Cadastro de Animais',
      description: 'Guia passo a passo da tela',
      icon: 'ri-book-2-line',
      to: '/admin/operacional/cadastros/animais/manual',
    },
    sugestoes: [
      { id: 'ani-ger-cadastrar', icon: 'ri-add-circle-line', label: 'Como cadastro um animal?' },
      { id: 'ani-ger-vincular', icon: 'ri-links-line', label: 'Como vinculo um tutor ou cuidador?' },
      { id: 'ani-ger-selo', icon: 'ri-heart-pulse-line', label: 'Como funciona o selo Saudável?' },
      { id: 'ani-ger-codigo', icon: 'ri-qr-code-line', label: 'O que é o código PETBEA e o microchip?' },
      { id: 'ani-ger-exportar', icon: 'ri-download-2-line', label: 'Como exportar a lista de animais?' },
      { id: 'ani-ger-ficha', icon: 'ri-file-list-3-line', label: 'Como abrir e usar a ficha do animal?' },
    ],
  },
  {
    prefixo: '/admin/operacional/cadastros/pessoas',
    contexto: { label: 'Pessoas', icon: 'ri-user-3-line', to: '/admin/operacional/cadastros/pessoas' },
    saudacao:
      'Olá! Eu sou a Beaa. Estou com você no módulo de Pessoas. Posso explicar o cadastro, o manual passo a passo, a inativação de registros, o credenciamento e a exportação da lista. É só escrever ou tocar numa pergunta frequente.',
    tela: {
      label: 'Tela de Pessoas',
      description: 'Abrir o Cadastro de Pessoas',
      icon: 'ri-user-3-line',
      to: '/admin/operacional/cadastros/pessoas',
    },
    manual: {
      label: 'Manual de Cadastro de Pessoas',
      description: 'Guia passo a passo da tela',
      icon: 'ri-book-2-line',
      to: '/admin/operacional/cadastros/pessoas/manual',
    },
    sugestoes: [
      { id: 'pes-ger-cadastrar-pf', icon: 'ri-user-add-line', label: 'Como cadastro uma pessoa física?' },
      { id: 'pes-ger-inativar', icon: 'ri-toggle-line', label: 'Como inativar um cadastro?' },
      { id: 'pes-ger-exportar', icon: 'ri-download-2-line', label: 'Como exportar a lista de pessoas?' },
      { id: 'pes-ger-credenciado', icon: 'ri-shield-check-line', label: 'O que é estar credenciado?' },
      { id: 'pes-ger-modulos', icon: 'ri-apps-2-line', label: 'Quais módulos tem no Console?' },
      { id: 'pes-ger-senha', icon: 'ri-lock-password-line', label: 'Esqueci minha senha, e agora?' },
    ],
  },
  {
    prefixo: '/admin/operacional/cadastros/organizacoes',
    contexto: {
      label: 'Organizações',
      icon: 'ri-community-line',
      to: '/admin/operacional/cadastros/organizacoes',
    },
    saudacao:
      'Olá! Eu sou a Beaa. Estou com você no módulo de Organizações. Posso explicar o cadastro e a aprovação de ONGs, associações e coletivos, o credenciamento, os vínculos com pessoas e a documentação exigida. É só escrever ou tocar numa pergunta frequente.',
    tela: {
      label: 'Tela de Organizações',
      description: 'Abrir o Cadastro de Organizações',
      icon: 'ri-community-line',
      to: '/admin/operacional/cadastros/organizacoes',
    },
    // Atalho para o manual do módulo: como o "Manual de Cadastro de
    // Organizações" já existe, a Beaa o oferece automaticamente aqui.
    manual: {
      label: 'Manual de Cadastro de Organizações',
      description: 'Guia passo a passo da tela',
      icon: 'ri-book-2-line',
      to: '/admin/operacional/cadastros/organizacoes/manual',
    },
    sugestoes: [
      { id: 'org-ger-cadastrar', icon: 'ri-add-circle-line', label: 'Como cadastro uma organização?' },
      { id: 'org-ger-aprovar', icon: 'ri-checkbox-circle-line', label: 'Como aprovo uma organização pendente?' },
      { id: 'org-ger-vincular', icon: 'ri-links-line', label: 'Como vinculo pessoas a uma organização?' },
      { id: 'org-ger-responsavel', icon: 'ri-user-star-line', label: 'O que é o responsável legal?' },
      { id: 'org-ger-documentos', icon: 'ri-file-text-line', label: 'Quais documentos a organização precisa enviar?' },
      { id: 'org-ger-credenciado', icon: 'ri-shield-check-line', label: 'Como funciona o credenciamento da organização?' },
    ],
  },
];

/** Monta os atalhos do módulo: o manual (quando existir) vem primeiro, depois a tela. */
function atalhosDoModulo(modulo: (typeof MODULOS_CONSOLE)[number]): BeaaQuickAction[] {
  return modulo.manual ? [modulo.manual, modulo.tela] : [modulo.tela];
}

/** Encontra o módulo do Console correspondente à rota atual (se houver). */
function encontrarModuloConsole(pathname: string) {
  const rota = (pathname.split('#')[0] ?? '').split('?')[0] ?? '';
  return MODULOS_CONSOLE.find((modulo) => rota.startsWith(modulo.prefixo)) ?? null;
}

/**
 * Retorna os atalhos (manual + tela) do módulo correspondente à rota atual.
 * Sem módulo correspondente, devolve lista vazia — assim a Beaa nunca leva o
 * operador para a tela errada.
 */
export function beaaConsoleQuickActions(pathname: string): BeaaQuickAction[] {
  const modulo = encontrarModuloConsole(pathname);
  return modulo ? atalhosDoModulo(modulo) : [];
}

/**
 * Retorna as perguntas frequentes do módulo correspondente à rota atual.
 * Sem módulo correspondente, mantém as sugestões gerais do Console.
 */
export function beaaConsoleSuggestions(pathname: string): BeaaSuggestion[] {
  const modulo = encontrarModuloConsole(pathname);
  return modulo ? modulo.sugestoes : beaaConsoleGeralProfile.suggestions;
}

/**
 * Personaliza a saudação com o primeiro nome do operador, quando disponível.
 * Ex.: "Olá! Eu sou a Beaa..." vira "Olá, João! Eu sou a Beaa...".
 */
function saudacaoComNome(saudacao: string, nome?: string): string {
  const primeiro = nome?.trim().split(/\s+/)[0];
  if (!primeiro) return saudacao;
  return saudacao.replace(/^Olá!/, `Olá, ${primeiro}!`);
}

/**
 * Perfil geral do Console já com atalhos e perguntas do módulo visível na tela.
 * Quando o nome do operador é informado, a saudação fica pessoal.
 */
export function beaaConsoleProfilePara(pathname: string, nomeOperador?: string): BeaaAssistantProfile {
  const modulo = encontrarModuloConsole(pathname);
  if (!modulo) {
    return {
      ...beaaConsoleGeralProfile,
      greeting: saudacaoComNome(beaaConsoleGeralProfile.greeting, nomeOperador),
    };
  }
  return {
    ...beaaConsoleGeralProfile,
    greeting: saudacaoComNome(modulo.saudacao, nomeOperador),
    quickActions: atalhosDoModulo(modulo),
    suggestions: modulo.sugestoes,
    contextoModulo: modulo.contexto,
  };
}

/** Normaliza o texto: minúsculas, sem acentos e sem pontuação. */
export function normalizeBeaaText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Pontua uma entrada de conhecimento contra a pergunta já normalizada.
 * Quanto mais longa a palavra-chave encontrada, maior o peso dela.
 */
export function pontuarEntrada(entry: BeaaAssistantEntry, perguntaNormalizada: string): number {
  if (!perguntaNormalizada) return 0;
  let score = 0;
  for (const keyword of entry.keywords) {
    const term = normalizeBeaaText(keyword);
    if (term && perguntaNormalizada.includes(term)) {
      score += term.length;
    }
  }
  return score;
}

/**
 * Encontra a melhor resposta guiada para a pergunta dentro do perfil informado.
 * Quanto mais longa a palavra-chave encontrada, maior o peso dela.
 */
export function matchBeaaAnswer(
  question: string,
  profile: BeaaAssistantProfile = beaaPortalProfile,
): BeaaAssistantEntry {
  const normalized = normalizeBeaaText(question);
  if (!normalized) return profile.fallback;

  let best: BeaaAssistantEntry | null = null;
  let bestScore = 0;

  for (const entry of profile.knowledge) {
    const score = pontuarEntrada(entry, normalized);
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  return best ?? profile.fallback;
}